import { store as appStore } from '../../../redux/store';
import { COMMANDS } from '../../../utils/commonProperties';
import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearSelection } from '../../../redux/selectionSlice';
import { setCommandMessage, resetOperation, setOperationData, setCommandType } from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { createSingleSocket, socketBuatControl, singleCircleBuat, singleSquareBuat } from '../../../factory/symbols/createSocketSymbols';
import useSnapPoints from '../../../shared/snap/useSnapPoints';
import { useComponentWiring } from '../../../modules/wiring';
import { updatePanelTitles, updatePanelInputWire } from '../../../modules/wiring';
import { addWire } from '../../../redux/wireSlice';
import parseColor from '../../../utils/parseColor';

// 🆕 simpleSupply kullanılıyor
import { addLoadToComponent, addComponent } from '../../../redux/simpleSupplySlice';
import { addTesisat } from '../../../redux/tesisatSlice';
import { addTesisatIdToFloorPlan, addWireIdToFloorPlan } from '../../../redux/floorPlanSlice';
import { worldToLocal } from '../../../shared/coordinates/floorPlanOrigin';
import { getActiveFloorPlanId, getGlobalDocumentBounds } from '../../../utils/getActiveFloorPlanId';
import { attachTesisatToFloorPlanMesh } from '../../../modules/fpSync';
import { getObjectById } from '../../../utils/getObjectById';
import { COMPONENT_KINDS, FEEDER_KINDS } from '../../../types/SimpleSupplyPoint';
import { createSocketFeeder } from '../../../factories/FeederFactory';
import { SocketCommand } from '../../../modules/undoRedo';
import { findObjectByUUID } from '../../../utils/findObjectByUUID';

const useSocket = (scene, camera, renderer, undoRedoManagerRef) => {
 
  const dispatch = useDispatch();
  const { commandType, data: operationData } = useSelector((state) => state.operation);
  // simpleSupply: useSelector kaldırıldı — event handler'larda lazy read
  const settings = useSelector((state) => state.settings);
  const { snapPoints } = useSnapPoints(scene);

  const dynamicSnapPoints = useRef([]);
  const wiringActiveRef = useRef(false);
  const sceneRef = useRef(null);
  const placedSocketsRef = useRef([]);
    const socketConfig = {
  commandType: COMMANDS.DRAW_SOCKET,
  itemTypeName: 'socket',
  wireColor: parseColor(settings?.socket?.wireColor ?? 0x0045ff).colorNumber,
  allowSpaceKey: true, // ✅ Socket'lerde Space tuşu ile çoklu ekleme yapılabilir
  markerSkipFirstSegment: true, // ✅ preview = permanent (ilk/panel edge'inde marker yok)
  itemTypes: [
    { name: 'Normal Priz', type: 'normal', power: 300 },
    { name: 'Kapaklı', type: 'kapaklı', power: 300 },
    { name: 'Etanj', type: 'etanj', power: 300 }
  ],
  createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
    const colorSetting = isPreview
      ? (settings?.socket?.previewColor ?? settings?.socket?.color ?? 0x0045ff)
      : (settings?.socket?.color ?? 0x0045ff);
    const parsed = parseColor(colorSetting);
    const rotateValueDeg = extraData.rotateValue || 0;

    return createSingleSocket(
      null,
      position,
      direction,
      parsed.colorNumber,
      isPreview,
      itemType?.type || 'normal',
      null,
      rotateValueDeg
    );
  },
  createCommand: (action, componentData, feederData, sceneData) => {
    // Optionally add rotateValue persist logic if needed
    return new SocketCommand(action, componentData, feederData, sceneData);
  },
  onKeyDown: null // Şimdilik boş bırakılabilir veya rota döndürme gibi şeyler eklenebilir
};
  // === Yardımcı: sahneye görsel soket yerleştir
  const handleSocketPlace = (position, direction) => {
    const adjustedPos = position.clone().add(
      new THREE.Vector3(-direction.x * 7, -direction.y * 14, 0)
    );
      const socketColorSetting = settings?.socket?.color ?? 0x0045ff;
      const parsedSocket = parseColor(socketColorSetting);

      // Yön vektöründen açıyı hesapla (radians)
      const angle = Math.atan2(direction.y, direction.x);
      // Açıyı dereceye çevir
      const angleDegrees = angle * 180 / Math.PI;

      // ⚠️ ÖNEMLİ: ID'yi ÖNCEden oluştur ve createSingleSocket'e ver
      // Böylece gizmo'ların parentSocketId ile socket.userData.id eşleşir
      const id = generateUniqueId("socket");

      // Socket oluştur - direction bir Vector3 olduğu için createSingleSocket içinde otomatik rotation uygulanır
      // angleDegrees sadece userData.rotate için saklanır
      // providedSocketId parametresi ile ID tutarlılığını sağla
      const socket = createSingleSocket(sceneRef.current, adjustedPos, direction, parsedSocket.colorNumber, false, "normal", null, angleDegrees, id);

    // Mevcut userData'yı koru ve sadece gerekli alanları ekle/güncelle
    socket.userData = {
      ...socket.userData, // createSingleSocket'ten gelen userData'yı koru (id zaten burada)
      kind: COMPONENT_KINDS.SOCKET,
      type: 'socket',
      isSelectable: true,
      originalColor: parsedSocket.colorNumber,
      rotate: angleDegrees // Açıyı derece olarak sakla
    };

    // 🆕 Redux'a component ekle
    dispatch(addComponent({
      id,
      kind: COMPONENT_KINDS.SOCKET,
      electrical: { installedPower: 0 },
      incomingFeeders: [],
      outgoingFeeders: []
    }));

    // Not: tesisatSlice'a yazma confirmPlacement (Enter) sırasında socketInfos
    // ile birlikte yapılıyor. Burada (Space ile geçici yerleşim) yazılırsa,
    // kullanıcı çizimi iptal ederse Redux'ta orphan kayıt kalır.

    // Mesh-FP Aşama 3b: socket'i fpMesh-child olarak yerleştir (varsa).
    // floorPlanId tespiti — sweep akışı da bunu kullanır (useElectricalLayout sonu).
    const floorPlanId = getActiveFloorPlanId(adjustedPos, getGlobalDocumentBounds()) || null;
    if (floorPlanId) socket.userData = { ...socket.userData, floorPlanId };
    attachTesisatToFloorPlanMesh(sceneRef.current, socket, floorPlanId);
    return socket;
  };

  // === Soket sil
  const handleSocketRemove = (socket) => {
    if (!socket) return;
    sceneRef.current.remove(socket);
    socket.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
  };

  // === Socket placement confirmation
  const confirmPlacement = (vertices, sockets, source, tempLineUUID, markers, originalVertices) => {
  // 🆕 REDO kontrolü: REDO sırasında yeni transaction başlatma
  // REDO'da command zaten var, sadece notifyRedoComplete çağrılacak
  const isRedoMode = operationData?.isRedo;
  if (isRedoMode) {
    return;
  }

  // Source tipine göre parent panel ID'yi belirle
  let parentId = null;
  let sourceType = null;
  let parentSocketLoadId = null;
  let parentWireID = null;

  if (source?.type === 'panelConnection' && source?.p) {
    // Panel çıkışından başlatıldı
    parentId = source.p.panelID;
    sourceType = 'panel';
  } else if (source?.type === 'socketBuat') {
    // Mevcut priz buatından başlatıldı (dallanma)
    parentId = source.panelID || source.userData?.panelID;
    parentSocketLoadId = source.socketLoadId || source.userData?.socketLoadId;
    parentWireID = source.wireID || source.userData?.wireID;
    sourceType = 'socketBuat';

  } else if (source?.type === 'vertex' || source?.type === 'start' || source?.type === 'end') {
    // Priz hattının vertex'inden başlatıldı (dallanma). YALNIZ gerçek priz hattı
    // vertex'i geçerli — duvar/başka-tip çizgi köşesi DEĞİL. Aksi halde wire bir
    // priz kaynağına bağlanamıyor, tamamlanınca orphan olup siliniyordu (prizler kalıyor).
    const parentData = source.parent;
    if (parentData && (parentData.function === 'simpleSocket' || parentData.socketLoadId != null)) {
      parentId = parentData.panelID;
      parentSocketLoadId = parentData.socketLoadId;
      parentWireID = parentData.id || parentData.wireID;
      sourceType = 'socketVertex';
    }
  }

  if (!parentId) {
      return;
  }
  let branchID;
  let power = sockets.length * 300;

  // Socket load component ID
  const socketLoadId = generateUniqueId('socket_load');

  // Son prizin koordinatını bul ve vertices'i ona kadar kısalt
  let finalVertices = [...vertices];
  if (sockets.length > 0) {
    const lastSocket = sockets[sockets.length - 1];
    const lastSocketPos = lastSocket.position;

    // Son priz koordinatını vertices dizisinde bul
    let lastSocketIndex = -1;
    for (let i = 0; i < vertices.length; i++) {
      const vertex = vertices[i];
      if (Math.abs(vertex.x - lastSocketPos.x) < 0.01 &&
          Math.abs(vertex.y - lastSocketPos.y) < 0.01) {
        lastSocketIndex = i;
        break;
      }
    }

    // Son prizden sonraki noktaları sil
    if (lastSocketIndex !== -1) {
      finalVertices = vertices.slice(0, lastSocketIndex + 1);
    }
  }

  // Kısaltılmış vertices ile uzunluk hesapla
  let totalLength = 0;
  for (let i = 0; i < finalVertices.length - 1; i++) {
    totalLength += finalVertices[i].distanceTo(finalVertices[i + 1]);
  }

  // ✅ KALICI ÇİZGİ - Kısaltılmış vertices kullan
  const geometry = new THREE.BufferGeometry().setFromPoints(finalVertices);
  // Normalize configured colors (may be strings like "#c20000ff")
  const parsedWire = parseColor(settings?.socket?.wireColor ?? 0x0045ff);
  const parsedSocketColor = parseColor(settings?.socket?.color ?? 0x0045ff);
  const material = new THREE.LineBasicMaterial({ color: parsedWire.colorNumber, transparent: parsedWire.alpha < 1, opacity: parsedWire.alpha });
  const line = new THREE.Line(geometry, material);

  const feederId = generateUniqueId("feederSocket");
  const wireID = generateUniqueId("wireSocket");

  // ✅ Socket ID'lerini güvenli bir şekilde al
  const socketIdsList = sockets.map(s => s.id || s.uuid || s.userData?.id);

  // ✅ Socket ID map oluştur (İstenen özellik)
  const socketIdsMap = {};
  socketIdsList.forEach(id => {
      if (id) socketIdsMap[id] = true;
  });

  // ✅ Buat ID'lerini toplayacağımız liste ve map
  const buatIdsList = [];
  const buatIdsMap = {};

  // ✅ Wire Marker ID'lerini toplayacağımız liste ve map
  const wireMarkerIdsList = [];
  const wireMarkerIdsMap = {};

  line.userData = {
    id: wireID,
    feederId: feederId, // ✅ Redux feeder ID eşleştirmesi için
    type: 'wireSocket',
    function: 'simpleSocket',
    panelID: parentId,
    sockets: socketIdsList,
    socketIdsMap: socketIdsMap, // ✅ Map olarak eklendi
    buatIds: buatIdsList, // ✅ Buat ID'leri eklendi
    buatIdsMap: buatIdsMap, // ✅ Buat ID Map eklendi
    wireMarkerIds: wireMarkerIdsList, // ✅ Wire Marker ID'leri eklendi
    wireMarkerIdsMap: wireMarkerIdsMap, // ✅ Wire Marker ID Map eklendi
    length: totalLength.toFixed(2),
    socketLoadId: socketLoadId, // Socket load ID
    isSelectable: true,
    sourceType: sourceType, // Panel'den mi, socket buat'tan mı başladı
    vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })) // Snap points için vertex bilgileri
  };

  scene.add(line);
  // ── DEBUG (geçici): panel'e bağlı wire sayısı ──
  // Kullanıcı raporu: priz/aydınlatma için panele ilk eklenen çıkış hattının
  // çiftlenme sorunu var. Makine'de yok. confirmPlacement sonrası sahnede
  // panel'e bağlı kaç wire var, sayalım.
  try {
    let panelWireCount = 0;
    const panelTypes = new Set();
    scene.traverse(obj => {
      if (!obj.userData) return;
      const isWire = (obj.userData.type || '').startsWith('wire') || obj.userData.feederId;
      if (!isWire) return;
      const wirePanelId = obj.userData.panelID || obj.userData.panelId;
      if (wirePanelId === parentId) {
        panelWireCount++;
        if (obj.userData.type) panelTypes.add(obj.userData.type);
      }
    });
  } catch (_) {}

  // ✅ KALICI WIRE MARKER'LAR EKLE (150 birimden büyük segmentler için)
  const rotateVector = (vector, angleRad) => {
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    return new THREE.Vector3(
      vector.x * cos - vector.y * sin,
      vector.x * sin + vector.y * cos,
      0
    );
  };

  // İlk edge'i atla (yeşil nokta taşıması için) - finalVertices kullan
  for (let i = 1; i < finalVertices.length - 1; i++) {
    const p1 = finalVertices[i];
    const p2 = finalVertices[i + 1];
    const dir = new THREE.Vector3().subVectors(p2, p1);
    const length = dir.length();

    if (length < 150) continue;

    const normal = dir.clone().normalize();
    const edgeAngle = Math.atan2(normal.y, normal.x); // Radyan cinsinden kenar açısı
    const angleRad = THREE.MathUtils.degToRad(45); // 45 derece açı
    const angledDir = rotateVector(normal, angleRad);
    const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

    const forwardOffset = normal.clone().setLength(3);
    const halfLength = 2.5;

    // ✅ 3 center point - tümü world coordinates
    const positions = [
      mid.clone().sub(forwardOffset),
      mid.clone(),
      mid.clone().add(forwardOffset),
    ];

    // ✅ Marker Group Oluştur - MachineCommand pattern'i
    const markerGroup = new THREE.Group();
    markerGroup.position.set(0, 0, 0); // ✅ World coordinates (sıfır)

    markerGroup.userData = {
      role: 'wireMarker', // ✅ 'wireMarkerGroup' değil 'wireMarker'
      parentWireId: wireID, // ✅ feederId değil wireID (wire.userData.id ile eşleşmeli)
      type: 'permanent',
      id: markerGroup.uuid,
      edgeIndex: i, // ✅ updateGizmos için gerekli
      lastEdgeAngle: edgeAngle + angleRad, // ✅ Başlangıç açısı
      midPoint: mid.clone(), // ✅ Orta nokta referansı
      isSelectable: false, // ✅ Wire marker'lar seçilemez (gizmo oluşmamalı)
      isGizmo: false // ✅ Gizmo değil
    };

    // ✅ Her center point için angled line oluştur - world coordinates
    positions.forEach((centerPoint) => {
      const start = centerPoint.clone().add(angledDir.clone().multiplyScalar(-halfLength));
      const end = centerPoint.clone().add(angledDir.clone().multiplyScalar(halfLength));

      const geo = new THREE.BufferGeometry().setFromPoints([start, end]);
      const mat = new THREE.LineBasicMaterial({ color: 0xffffff });
      const markerLine = new THREE.Line(geo, mat);

      markerGroup.add(markerLine);
    });

    // ✅ Listeye ve Map'e ekleme
    wireMarkerIdsList.push(markerGroup.uuid);
    wireMarkerIdsMap[markerGroup.uuid] = true;

    scene.add(markerGroup);
  }

  // Panel objesini bul
  let panel = null;
  if (sourceType === 'panel') {
    const foundPanelOutSnap = findObjectByUUID(scene, source.p.UUID);
    panel = source?.userData?.sourceObject ?? foundPanelOutSnap;
  } else if (sourceType === 'socketBuat' || sourceType === 'socketVertex') {
    // Socket buat veya vertex'ten başlatıldıysa, parent paneli bul
    panel = getObjectById(scene, parentId);

    if (!panel) {
      // Alternatif: Scene'i tara ve paneli bul
      scene.traverse(obj => {
        if (obj.userData?.id === parentId) {
          panel = obj;
        }
      });
    }
  }

  branchID = generateUniqueId("branchSocket");

  // ✅ Wire userData'ya branchID ekle (panelExitManager wire eşleştirmesi için)
  line.userData.branchID = branchID;

  // Panel var mı kontrol et
  if (!panel) {
    return;
  }

  // Panel userData var mı kontrol et
  if (!panel.userData) {
    panel.userData = {};
  }

  // Panel userData.outs array'ini kontrol et, yoksa oluştur
  if (!panel.userData.outs) {
    panel.userData.outs = [];
  }

  // ✅ Panel'e bağlı wire ID'lerini kaydet (performans için)
  if (!panel.userData.connectedWires) {
    panel.userData.connectedWires = [];
  }
  if (!panel.userData.connectedWires.includes(wireID)) {
    panel.userData.connectedWires.push(wireID);
  }


  // ✅ Command için component ve feeder verilerini scope dışında tanımla
  let commandComponentData = null;
  let commandFeederData = null;

  // ✅ Dallanma kontrolü: Mevcut hatta ekleniyorsa parent component'i güncelle
  if (sourceType === 'socketBuat' || sourceType === 'socketVertex') {
    // Mevcut bir hatta dallanma - parent socket load'ı bul ve güncelle
    const parentComponent = appStore.getState().simpleSupply.components?.find(c => c.id === parentSocketLoadId);

    if (parentComponent) {

      // Mevcut component'in gücünü ve socket sayısını güncelle
      const updatedPower = (parentComponent.electrical?.installedPower || 0) + power;
      const updatedSocketCount = (parentComponent.metadata?.socketCount || 0) + sockets.length;

      dispatch(addLoadToComponent({
        parentComponentId: panel.userData?.id || panel.id,
        loadData: {
          ...parentComponent,
          electrical: {
            ...parentComponent.electrical,
            installedPower: updatedPower,
            demandPower: updatedPower,
            current: updatedPower / (220 * 1)
          },
          metadata: {
            ...parentComponent.metadata,
            socketCount: updatedSocketCount
          }
        },
        feederData: null, // Yeni feeder ekleme, sadece component güncelle
        updateExisting: true // Component güncelleme modu
      }));

      // ✅ Dallanma için mevcut component'i command'a geç
      commandComponentData = { ...parentComponent, id: parentComponent.id };
      // Dallanmada feeder zaten var, command'a geçme (createWire/redo wire'ı
      // SocketCommand.execute içinde sceneData'dan kurulur — addLoadToComponent
      // exitOrder crash'ini tetiklememek için _addComponent feeder yoluna girmiyoruz)
      commandFeederData = null;

    } else {
      // Parent bulunamadıysa yeni component oluştur (fallback)
      const newSocketLoadData = {
        id: socketLoadId,
        name: 'Priz Hattı',
        branchType: 'socket',
        power: power,
        phases: 1,
        electrical: {
          installedPower: power,
          demandPower: power,
          current: power / (220 * 1),
          phaseCount: 1,
          demandFactor: "100%"
        },
        parentComponentId: panel.userData?.id || panel.id,
        metadata: {
          wireId: wireID,
          branchId: branchID,
          length: totalLength.toFixed(2),
          socketCount: sockets.length,
          loadType: 'socket'
        }
      };

      const socketFeeder = createSocketFeeder(
        power,
        panel.userData?.id || panel.id,
        socketLoadId,
        {
          id: feederId,
          vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
          cable: {
            length: Math.round(totalLength / 10)
          },
          metadata: {
            branchType: 'socket',
            length: totalLength.toFixed(2),
            wireId: wireID
          }
        }
      );

      dispatch(addLoadToComponent({
        parentComponentId: panel.userData?.id || panel.id,
        loadData: newSocketLoadData,
        feederData: socketFeeder
      }));

      // ✅ Command için verileri kaydet
      commandComponentData = newSocketLoadData;
      commandFeederData = socketFeeder;
    }
  } else {
    // Panel'den başlayan yeni hat - normal akış
    const newSocketLoadData = {
      id: socketLoadId,
      name: 'Priz Hattı',
      branchType: 'socket',
      power: power,
      phases: 1,
      electrical: {
        installedPower: power,
        demandPower: power,
        current: power / (220 * 1),
        phaseCount: 1,
        demandFactor: "100%"
      },
      parentComponentId: panel.userData?.id || panel.id,
      metadata: {
        wireId: wireID,
        branchId: branchID,
        length: totalLength.toFixed(2),
        socketCount: sockets.length,
        loadType: 'socket'
      }
    };

    const socketFeeder = createSocketFeeder(
      power,
      panel.userData?.id || panel.id,
      socketLoadId,
      {
        id: feederId,
        vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
        cable: {
          length: Math.round(totalLength / 10)
        },
        metadata: {
          branchType: 'socket',
          length: totalLength.toFixed(2),
          wireId: wireID
        }
      }
    );

    dispatch(addLoadToComponent({
      parentComponentId: panel.userData?.id || panel.id,
      loadData: newSocketLoadData,
      feederData: socketFeeder
    }));

    // ✅ Command için verileri kaydet
    commandComponentData = newSocketLoadData;
    commandFeederData = socketFeeder;
  }


  // ESKİ SİSTEM: Legacy compat için hala userData'ya da ekle
  // ⚠️ Sadece panel'den direkt çıkan hatlar için outs'a ekle, dallanmalarda ekleme
  if (sourceType === 'panel') {
    const exitData = {
      branchID,
      power,
      branchType: "socket",
      description: "socketLine",
      length: totalLength.toFixed(2),
      mcbReference: 433, // MCB numarası eklendi
      feederId: feederId, // ✅ Tek Hat Şeması için feederId eklendi
      lineType: "socket", // ✅ Transform fonksiyonu için lineType eklendi
      wires: [{
        id: wireID,
        length: totalLength.toFixed(2),
        power
      }]
    };

    // ✅ addPosition'a göre diziye ekleme yap
    const addPosition = operationData?.addPosition || 'start';
    if (addPosition === 'end') {
      // "Başa ekle" seçili - dizinin BAŞINA ekle
      panel.userData.outs.unshift(exitData);
    } else {
      // "Sona ekle" seçili (default) - dizinin SONUNA ekle
      panel.userData.outs.push(exitData);
    }
  } else {

  }
  // Refresh panel titles visually so panel returns to normal state
  try {
    if (panel && panel.userData && panel.userData.id) {
      // Panel giriş hattını güncelle (panel boyutu değiştiğinde)
      updatePanelInputWire(scene, panel);
      // Panel title'ları güncelle
      updatePanelTitles(scene, panel.userData.id);
    }
  } catch (e) {}
const lastSocket = sockets[sockets.length - 1];
const targetId = lastSocket?.userData?.id || lastSocket?.id;
  // 🗑️ DUPLICATE COMPONENT/FEEDER EKLEME KALDIRILDI
  // addLoadToComponent zaten component ve feeder ekliyor

  // Redux wireSlice kaydı - Kısaltılmış vertices kullan
  dispatch(addWire({
    id: feederId,
    sceneUUID: line.uuid,
    type: "polyline",
    vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
    layer: "socket",
    color: '#' + parsedWire.colorNumber.toString(16).padStart(6, '0'),
    userData: {
      type: 'wireSocket',
      function: 'simpleSocket',
      panelID: parentId,
      sockets: socketIdsList,
      socketLoadId: socketLoadId,
      sourceType: sourceType,
      feederId: feederId,
      isSelectable: true,
    }
  }));

  // ✅ Prizlere socket buat ekle (son prize hariç)
  sockets.slice(0, -1).forEach((socket) => {
    const socketPosition = socket.position;
    const socketId = socket.userData?.id || socket.id;
    const buatId = generateUniqueId('buat'); // ID'yi burada üret

    const buat = singleCircleBuat(
      { x: socketPosition.x, y: socketPosition.y },
      parentId,
      socket.uuid,
      parsedSocketColor.colorNumber
    );

    // Buat'ı socketBuat olarak işaretle
    buat.userData = {
      ...buat.userData,
      id: buatId, // ✅ ID'yi userData'ya ekle
      type: 'socketBuat',
      function: 'simpleSocket',
      socketLoadId: socketLoadId,
      wireID: wireID,
      panelID: parentId
    };
    
    // ✅ Buat ID'sini listeye ekle
    buatIdsList.push(buatId);
    buatIdsMap[buatId] = true;

    scene.add(buat);
  });

  // ✅ Dallanma noktasına buat ekle veya güncelle
  if (sourceType === 'socketVertex' || sourceType === 'socketBuat') {
    const branchPoint = vertices[0]; // İlk vertex = dallanma noktası

    // Bu noktada zaten bir buat var mı kontrol et
    let existingBuat = null;
    scene.traverse(obj => {
      if ((obj.userData?.type === 'socketBuat' || obj.userData?.type === 'buatCircle') &&
          Math.abs(obj.position.x - branchPoint.x) < 0.01 &&
          Math.abs(obj.position.y - branchPoint.y) < 0.01) {
        existingBuat = obj;
      }
    });

    if (existingBuat) {
      // Mevcut buat var
      const isCircleBuat = existingBuat.userData?.buatType === 'circle' ||
                          existingBuat.userData?.type === 'buatCircle' ||
                          !existingBuat.userData?.buatType; // buatType tanımlı değilse varsayılan circle

      // Yuvarlak buat'a yeni hat ekleniyorsa (vertex veya buat'tan) kare buat'a dönüştür
      if (isCircleBuat) {
        const oldPosition = existingBuat.position.clone();
        const oldUserData = existingBuat.userData;
        const buatId = oldUserData.id || generateUniqueId('buat'); // Mevcut ID'yi koru veya yeni üret

        // Eski buat'ı kaldır
        scene.remove(existingBuat);
        existingBuat.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });

        // Kare buat oluştur
        const squareBuat = singleSquareBuat(
          scene,
          { x: oldPosition.x, y: oldPosition.y },
          parsedSocketColor.colorNumber,
          oldUserData.socketId || generateUniqueId('buat')
        );

        squareBuat.userData = {
          ...oldUserData,
          id: buatId, // ✅ ID'yi garanti et
          type: 'socketBuat',
          buatType: 'square'
        };

        // ✅ Buat ID'sini listeye ekle
        buatIdsList.push(buatId);
        buatIdsMap[buatId] = true;

        // ✅ Yeni socket wire'ı buat'a ekle (userData property read-only ise defineProperty ile zorla)
        try {
          const newUserData = {
            ...squareBuat.userData,
            socketWireMapIds: [
              ...(squareBuat.userData.socketWireMapIds || []),
              wireID
            ]
          };

          Object.defineProperty(squareBuat, 'userData', {
            value: newUserData,
            writable: true,
            configurable: true,
            enumerable: true
          });
        } catch (err) {
          // Square buat userData update failed
        }

        scene.add(squareBuat);
      } else {
          // Zaten kare buat ise veya dönüşüm gerekmiyorsa, mevcut ID'yi ekle
          if (existingBuat.userData?.id) {
              buatIdsList.push(existingBuat.userData.id);
              buatIdsMap[existingBuat.userData.id] = true;

              // ✅ Yeni socket wire'ı mevcut buat'a ekle (userData property read-only ise defineProperty ile zorla)
              try {
                const newUserData = {
                  ...existingBuat.userData,
                  socketWireMapIds: [
                    ...(existingBuat.userData.socketWireMapIds || []),
                    wireID
                  ]
                };

                Object.defineProperty(existingBuat, 'userData', {
                  value: newUserData,
                  writable: true,
                  configurable: true,
                  enumerable: true
                });
              } catch (err) {
                // Existing buat userData update failed
              }
          }
      }
    } else {
      // Bu noktada buat yok - yeni yuvarlak buat ekle
      const buatId = generateUniqueId('buat');
      const newBuat = singleCircleBuat(
        { x: branchPoint.x, y: branchPoint.y },
        parentId,
        generateUniqueId('buat'),
        parsedSocketColor.colorNumber
      );

      newBuat.userData = {
        id: buatId, // ✅ ID ekle
        type: 'socketBuat',
        function: 'simpleSocket',
        socketLoadId: parentSocketLoadId || socketLoadId,
        wireID: parentWireID,
        panelID: parentId,
        buatType: 'circle',
        socketWireMapIds: [wireID] // ✅ Yeni socket wire'ı ekle
      };

      // ✅ Buat ID'sini listeye ekle
      buatIdsList.push(buatId);
      buatIdsMap[buatId] = true;

      scene.add(newBuat);
    }
  }

  // Global Undo/Redo Transaction
  // ✅ Runtime'da undoRedoManager'ı kontrol et (ref'ten veya window'dan)
  const undoRedoManager = undoRedoManagerRef.current || (typeof window !== 'undefined' ? window.undoRedoManager : null);

  if (undoRedoManager) {
    
    const tx = undoRedoManager.beginTransaction('Add Socket Wire');

    // Restore callback: undo sonrası draw modunu yeniden aç
    const restoreCallback = (restoreVertices, restoreSource, isRedoContext = false, sceneData = null) => {
      dispatch(setCommandType(COMMANDS.DRAW_SOCKET));

      setTimeout(() => {
        const operationData = {
          initialPoint: restoreVertices && restoreVertices[0] ? {
            x: restoreVertices[0].x,
            y: restoreVertices[0].y,
            z: restoreVertices[0].z || 0
          } : null,
          source: restoreSource,
          _restoreId: Date.now() + Math.random()
        };

        if (isRedoContext) {
          // REDO: Sadece ilk vertex, geri kalanlar vertexHistory'de
          operationData.restoreVertices = [restoreVertices[0]];
          operationData.vertexHistory = restoreVertices.slice(1);
          operationData.isRedo = true;
          operationData.socketInfos = sceneData?.socketInfos || [];
          operationData.buatInfos = sceneData?.buatInfos || []; // ✅ Buat bilgilerini aktar
          // 🆕 onRedoComplete callback'ini aktar (chain advancement için)
          operationData.onRedoComplete = sceneData?.onRedoComplete;
        } else {
          // UNDO: Tüm vertices yükle
          operationData.restoreVertices = restoreVertices;
          operationData.isRedo = false;
          operationData.socketInfos = restoreSource?.socketInfos || sceneData?.socketInfos || [];
          operationData.buatInfos = sceneData?.buatInfos || []; // ✅ Buat bilgilerini aktar
          // cancelWiring'de outs silme için ID'leri ekle
          operationData.branchID = sceneData?.branchId || sceneData?.branchID;
          operationData.wireId = sceneData?.wireId || sceneData?.wireID;
          operationData.feederId = sceneData?.wireId;
          operationData.loadId = sceneData?.socketLoadId;
        }

        dispatch(setOperationData(operationData));
      }, 16);
    };

    // Tüm wire + socket'ler için tek bir komut oluştur
    // Wire bilgisi
    const wireData = {
      id: feederId,
      sceneUUID: line.uuid,
      type: "polyline",
      vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
      layer: "socket",
      color: '#' + parsedWire.colorNumber.toString(16).padStart(6, '0'),
      socketLoadId: socketLoadId,
      wireID: wireID,
      panelId: parentId // ✅ Panel çıkış yönetimi için gerekli (REDO'da updatePanelWiringPositions kullanır)
    };

    // Tüm socket ID'lerini ve bilgilerini topla - scene'den bul
    const socketIds = [];
    const socketInfos = []; // ✅ Restore için socket bilgileri

    sockets.forEach(socketItem => {
      // socketItem çoğunlukla scene üzerindeki preview socket objesidir (createSingleSocket tarafından eklenen Group)
      // Öncelikle socketItem içinde userData/id olup olmadığını kontrol et - doğrudan kullan
      // Prefer ids produced during addItem: socketItem.uuid (component id) or socketItem.id
      // socketItem usually is the object pushed into base.itemsRef in useBaseWiring.addItem
      const compId = socketItem?.uuid || socketItem?.id || socketItem?.userData?.id || null;
      let foundObj = null;
      if (compId) {
        // find scene object with matching userData.id
        scene.traverse(obj => {
          if (!foundObj && obj.userData?.id === compId) foundObj = obj;
        });
      }

      // Fallback: if we didn't find an object by id, try a relaxed position match
      if (!foundObj && socketItem && socketItem.position) {
        const itemPos = socketItem.position;
        scene.traverse(obj => {
          if (!foundObj && obj.userData?.type === 'socket' && obj.userData?.id) {
            const objPos = obj.position;
            if (Math.abs(objPos.x - itemPos.x) < 0.1 && Math.abs(objPos.y - itemPos.y) < 0.1) {
              foundObj = obj;
            }
          }
        });
      }

      const finalId = (foundObj && foundObj.userData?.id) || compId || socketItem?.uuid || socketItem?.id || generateUniqueId('socket');
      socketIds.push(finalId);

      const pos = (foundObj && foundObj.position) || socketItem?.position || { x: 0, y: 0, z: 0 };
      const rot = (foundObj && foundObj.rotation && foundObj.rotation.z) || socketItem?.rotation?.z || 0;
      const typ = socketItem?.type || foundObj?.userData?.subtype || 'normal';
      const pow = (foundObj && foundObj.userData && foundObj.userData.power) || socketItem?.power || 300;
      const rotateVal = socketItem?.rotateValue || 0; // ✅ R tuşu ile eklenen rotasyon

      // ✅ Socket restore bilgilerini kaydet (pozisyon olarak preview/vertex bazlı pozisyon kullan)
      socketInfos.push({
        id: compId,
        position: { x: pos.x, y: pos.y, z: pos.z || 0 },
        rotation: rot,
        type: typ,
        power: pow,
        rotateValue: rotateVal, // ✅ R tuşu rotasyonu
        verticesAdded: 1 // ✅ REDO sırasında backward projection yok, her zaman 1 vertex
      });
      // ── DEBUG (geçici): 5,5 birim ötelenme tanısı ──
      // Save edilecek pos vs scene'deki mesh.position karşılaştırması
      const sceneObj = foundObj || socketItem;
    });

    
    

    // 🆕 Socket meta'larını tesisatSlice'a yaz — JBZ load sonrası mesh rebuild için.
    // localPosition: kat planına relative pozisyon (save/reload arasında layout
    // değişse bile tesisat doğru hizada kalır). position alanı geriye dönük
    // uyumluluk için world coord olarak kalıyor; rebuild önce localPosition'ı
    // dener, yoksa position'a düşer.
    socketInfos.forEach((info, idx) => {
      const sock = sockets[idx];
      const sceneObj = sock?.userData?.id ? getObjectById(scene, sock.userData.id) : null;
      // floorPlanId tespiti: önce sock/sceneObj userData, yoksa world position'dan
      // documentBounds üzerinden bul (socket nereye düşüyorsa o kat planının ID'si).
      let floorPlanId = sceneObj?.userData?.floorPlanId || sock?.userData?.floorPlanId || null;
      if (!floorPlanId) {
        floorPlanId = getActiveFloorPlanId(info.position, getGlobalDocumentBounds()) || null;
      }
      const localPosition = worldToLocal(info.position, floorPlanId);
      // userData'ya da yaz — sceneSerializer save sırasında bunu yakalar →
      // load sonrası migration helper localPosition'ı geri okuyabilir.
      if (sock && localPosition) {
        sock.userData = { ...sock.userData, floorPlanId, localPosition };
      }
      dispatch(addTesisat({
        id: info.id,
        type: 'socket',
        subtype: info.type || 'normal',
        position: info.position,
        localPosition,
        rotation: info.rotation || 0,
        rotateValue: info.rotateValue || 0,
        stretchLength: sock?.userData?.stretchLength || 0,
        power: info.power || 300,
        color: parsedSocketColor.colorNumber,
        floorPlanId,
        // Sahnede socket Y-axis flip (dir) varsa restore için sakla
        dir: sock?.children?.[0]?.scale?.y === -1 ? -1 : 1,
        _v: 2, // position format: absolute world — useTesisatRebuild old-format branch'ini atla
      }));
      // Phase A.2: floor plan indexine de yaz
      if (floorPlanId) {
        dispatch(addTesisatIdToFloorPlan({
          tabId: 'elektrik',
          floorPlanId,
          itemId: info.id,
        }));
      }
    });

    // ✅ Buat bilgilerini topla (UNDO/REDO için)
    const buatInfos = [];
    scene.traverse(obj => {
      if (obj.userData?.id && buatIdsMap[obj.userData.id]) {
        buatInfos.push({
          id: obj.userData.id,
          position: { x: obj.position.x, y: obj.position.y, z: obj.position.z || 0 },
          buatType: obj.userData.buatType || 'circle',
          type: obj.userData.type,
          socketLoadId: obj.userData.socketLoadId,
          wireID: obj.userData.wireID,
          panelID: obj.userData.panelID,
          function: obj.userData.function
        });
      }
    });

    const sceneData = {
      wireId: feederId,
      wireUUID: line.uuid,
      wireID: wireID, // ✅ Buat'lar için wireID ekle
      branchId: branchID, // ✅ UNDO için branchID ekle (panelExitManager eşleştirmesi)
      socketIds: socketIds,
      socketInfos: socketInfos, // ✅ Socket restore bilgileri
      buatInfos: buatInfos, // ✅ Buat restore bilgileri (UNDO/REDO için)
      buatIds: buatIdsList, // ✅ Buat ID'leri
      vertices: finalVertices.map(v => ({ x: v.x, y: v.y, z: v.z || 0 })),
      restoreVertices: (originalVertices || finalVertices).map(v => ({ x: v.x, y: v.y, z: v.z || 0 })), // ✅ Orijinal vertices (simplify edilmemiş)
      restoreSource: source ? {
        type: source.type,
        p: source.p ? { ...source.p } : null,
        no: source.no,
        // ✅ Machine pattern: panelID ve UUID en üst seviyede olmalı (useBaseWiring için)
        UUID: source.p?.UUID || source.no,
        panelID: source.p?.panelID || source.userData?.sourceObject?.userData?.id || null,
        // ✅ Dallanma (vertex/socketBuat) kaynak bilgilerini koru — yoksa re-draw + (partial)
        // Enter'da confirmPlacement parentId'yi bulamıyor (source.parent kayıp), hat
        // finalize olmuyor, undo resetleniyordu.
        socketLoadId: source.socketLoadId ?? null,
        wireID: source.wireID ?? null,
        parent: source.parent ? {
          panelID: source.parent.panelID ?? null,
          socketLoadId: source.parent.socketLoadId ?? null,
          id: source.parent.id ?? null,
          wireID: source.parent.wireID ?? null,
        } : null,
      } : null,
      restoreCommandType: COMMANDS.DRAW_SOCKET,
      restoreCallback: restoreCallback,
      // ✅ REDO için renk bilgisi
      color: parsedWire.colorNumber,
      // ✅ REDO'da wire'ı yeniden kurmak için (SocketCommand.execute)
      panelID: parentId,
      socketLoadId: socketLoadId,
    };

    // ✅ Command'a doğru component ve feeder verisi geç (redo için gerekli)
    // ⚠️ Branch update durumunda commandFeederData=null, bu durumda wireData fallback olarak KULLANILMAMALI
    // Branch update'te mevcut feeder güncelleniyor, yeni feeder oluşturulmuyor
    const cmd = new SocketCommand('ADD', commandComponentData || { id: socketLoadId }, commandFeederData, sceneData);

    undoRedoManager.addCommand(cmd);

    undoRedoManager.commitTransaction(tx);
    

    // End draw operation so panel returns to normal and clicks will open the panel menu
    try {
      dispatch(resetOperation());
      wiringActiveRef.current = false;
    } catch (e) {
      // Could not reset operation after socket wire add
    }
  }

  // Ensure we exit draw mode so panels become clickable even when undo manager is not present
  try {
    dispatch(resetOperation());
    dispatch(clearSelection());
    wiringActiveRef.current = false;
  } catch (e) {
    // ignore
  }

  return { wireId: feederId, line }; // ✅ Line'ı döndür
};

  // === Wire çizimi bittiğinde Redux'a feeder kaydı
  const handleWireEnd = (vertices, sockets, source, tempLineUUID) => {
    return confirmPlacement(vertices, sockets, source, tempLineUUID);
  };

  // === Ok ile kat geçişinde (ok hat sonu) prizlere buat ekle ===
  // Normal finalize'da son priz hariç buat eklenir (son priz = hat sonu). Ok hattında
  // hat sonu PRİZ DEĞİL OK olduğu için TÜM prizlere buat eklenir.
  // useBuatTypeSync tipi (yuvarlak/kare) hat sayısına göre düzeltir.
  const handleArrowFinalize = ({ items, wireId, panelID, color }) => {
    if (!Array.isArray(items) || !items.length) return;
    const buatColor = color ?? parseColor(settings?.socket?.color ?? 0x0045ff).colorNumber;
    items.forEach((socket) => {
      if (!socket?.position) return;
      const buat = singleCircleBuat(
        { x: socket.position.x, y: socket.position.y },
        panelID || null,
        socket.uuid,
        buatColor
      );
      buat.userData = {
        ...buat.userData,
        id: generateUniqueId('buat'),
        type: 'socketBuat',
        function: 'simpleSocket',
        wireID: wireId || null,
        panelID: panelID || null,
      };
      scene.add(buat);
    });
  };

  // === Socket eklendiğinde (Space) çalışacak callback
  const handleItemAdd = (item) => {
      // Socket added via Space key
  };

  // === Ana useEffect
  useEffect(() => {
    if (commandType !== COMMANDS.DRAW_SOCKET || !scene || !camera || !renderer) return;
    dispatch(setCommandMessage('Soket hattı çizin, Space ile soket ekleyin, Enter ile bitirin.'));

    sceneRef.current = scene;

    // Panel çıkışlarını ve mevcut priz buatlarını snap noktası olarak al
    dynamicSnapPoints.current = [];
    scene.traverse(obj => {
      // Panel çıkışları
      if (obj.userData?.connectionPoints) {
        obj.userData.connectionPoints.forEach(p => {
          if (p.type === 'panelCon_out') {
            const worldPos = p.position.clone().applyMatrix4(obj.matrixWorld);
            dynamicSnapPoints.current.push({
              no: obj.uuid,
              position: worldPos,
              type: 'panelConnection',
              p: p // Panel connection point bilgisi
            });
          }
        });
      }

      // Mevcut priz buatları (dallanma için)
      if (obj.userData?.type === 'socketBuat' || obj.userData?.type === 'buatCircle') {
        dynamicSnapPoints.current.push({
          position: obj.position.clone(),
          type: 'socketBuat',
          parent: obj.parent,
          no: obj.uuid,
          userData: obj.userData,
          // Socket buat'tan parent bilgilerini al
          socketLoadId: obj.userData?.socketLoadId,
          wireID: obj.userData?.wireID,
          panelID: obj.userData?.panelID
        });
      }

      // Priz hattı vertex'leri (dallanma için)
      if (obj.userData?.function === 'simpleSocket' && obj.userData?.vertices) {
        obj.userData.vertices.forEach((vertex, index) => {
          dynamicSnapPoints.current.push({
            position: new THREE.Vector3(vertex.x, vertex.y, vertex.z || 0),
            type: index === 0 ? 'start' : (index === obj.userData.vertices.length - 1 ? 'end' : 'vertex'),
            parent: obj.userData, // userData objesini parent olarak geç
            no: obj.uuid
          });
        });
      }
    });

    wiringActiveRef.current = true;

    // ✅ Cleanup: commandType değiştiğinde wiring'i devre dışı bırak
    return () => {
      wiringActiveRef.current = false;
    };
  }, [commandType, scene, camera, renderer]);

useComponentWiring(
  scene,
  camera,
  renderer,
  [...snapPoints, ...dynamicSnapPoints.current],
  {
    ...socketConfig, // 🆕 Config'i doğrudan geçir
    enabled: () => wiringActiveRef.current,
    onWireEnd: handleWireEnd,
    onArrowFinalize: handleArrowFinalize,
    onSocketPlace: handleSocketPlace,
    onItemAdd: handleItemAdd,
    onSocketRemove: handleSocketRemove,
    onPreviewUpdate: () => {},
    onWireCancel: () => { wiringActiveRef.current = false; },
    undoRedoManagerRef: undoRedoManagerRef, // ✅ Auto-finish ve next transaction için gerekli
    isRedo: operationData?.isRedo || false // ✅ REDO mode: simplify devre dışı bırakılır
  }
);

};

export default useSocket;
